import {useMemo, useSyncExternalStore} from "react";
import i18next from "i18next";
import en from "./locales/en.json";
import ru from "./locales/ru.json";
import orgsEn from "./fragments/orgs.en.json";
import orgsRu from "./fragments/orgs.ru.json";
import moneyEn from "./fragments/money.en.json";
import moneyRu from "./fragments/money.ru.json";
import sectionsEn from "./fragments/sections.en.json";
import sectionsRu from "./fragments/sections.ru.json";
import overviewEn from "./fragments/overview.en.json";
import overviewRu from "./fragments/overview.ru.json";
import {
    DEFAULT_LANGUAGE,
    SUPPORTED_LANGUAGES,
    getAdminLanguage,
    setAdminLanguage,
} from "./language";

const mergeDictionaries = (base, extra) => {
    const merged = {...base};
    Object.entries(extra).forEach(([key, value]) => {
        const current = merged[key];
        merged[key] = value && typeof value === "object" && !Array.isArray(value)
            && current && typeof current === "object" && !Array.isArray(current)
            ? mergeDictionaries(current, value)
            : value;
    });
    return merged;
};

export {DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, getAdminLanguage} from "./language";

const resources = {
    ru: {translation: mergeDictionaries(mergeDictionaries(mergeDictionaries(mergeDictionaries(ru, orgsRu), moneyRu), sectionsRu), overviewRu)},
    en: {translation: mergeDictionaries(mergeDictionaries(mergeDictionaries(mergeDictionaries(en, orgsEn), moneyEn), sectionsEn), overviewEn)},
};

// Инициализация — побочный эффект импорта этого модуля, а не отдельный провайдер
// в дереве React. Так переводы работают в любом тесте, который рендерит
// компонент напрямую, без обёрток.
if (!i18next.isInitialized) {
    i18next.init({
        resources,
        lng: getAdminLanguage(),
        // Ключ, которого нет в английском словаре, отдаёт русский текст.
        fallbackLng: DEFAULT_LANGUAGE,
        supportedLngs: SUPPORTED_LANGUAGES,
        interpolation: {escapeValue: false},
        returnNull: false,
        // А ключ, которого нет вообще нигде, не должен превращаться в
        // "audit.title" на экране: пробуем русский словарь по плоскому пути.
        parseMissingKeyHandler: (key) => i18next.getResource(DEFAULT_LANGUAGE, "translation", key) ?? key,
    });
}

const syncDocumentLanguage = (language) => {
    if (typeof document !== "undefined") document.documentElement.lang = language;
};

syncDocumentLanguage(i18next.language || DEFAULT_LANGUAGE);
i18next.on("languageChanged", syncDocumentLanguage);

export const i18n = i18next;

const subscribeToLanguage = (onLanguageChange) => {
    i18next.on("languageChanged", onLanguageChange);
    return () => i18next.off("languageChanged", onLanguageChange);
};

const languageSnapshot = () => i18next.language || DEFAULT_LANGUAGE;

export const useTranslation = () => {
    const language = useSyncExternalStore(subscribeToLanguage, languageSnapshot, languageSnapshot);
    return useMemo(() => ({t: i18next.getFixedT(language), i18n: i18next, language}), [language]);
};

// Смена языка применяется сразу: i18next уведомляет подписчиков useTranslation,
// перезагрузка не нужна.
export const changeAdminLanguage = (requestedLanguage) => {
    const nextLanguage = setAdminLanguage(requestedLanguage);
    if (nextLanguage !== i18next.language) void i18next.changeLanguage(nextLanguage);
    return nextLanguage;
};

export const useAdminLanguage = () => {
    const {language} = useTranslation();
    return [language, changeAdminLanguage];
};

export default i18next;
